/**
 * ApStatementsTab — Estado de cuenta por proveedor con Autocomplete.
 */
import { useState } from 'react'
import { Autocomplete } from '@/components/Autocomplete'
import type { AutocompleteItem } from '@/components/Autocomplete'
import { useSuppliers } from '@/modules/purchases/hooks/usePurchases'
import { useSupplierStatement } from '../hooks/useFinance'
import { useTenantLocale, formatNumber, formatDate } from '@/lib/locale'
import { inputCls } from '@/lib/formStyles'
import { FileText } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'

export default function ApStatementsTab() {
  const [supplierId, setSupplierId] = useState('')
  const [displayText, setDisplayText] = useState('')
  const locale = useTenantLocale()

  const { data: suppliersData } = useSuppliers({ active: 'true', page_size: 9999 })
  const { data, isLoading } = useSupplierStatement(supplierId)

  const suppliers = suppliersData?.suppliers || []
  const supplierItems: AutocompleteItem[] = suppliers.map((s: any) => ({
    id: s.id,
    label: s.legal_name,
    sublabel: s.tax_id,
  }))

  const fmt = (value: string) => {
    const n = parseFloat(value)
    if (n === 0) return ''
    const decimals = n % 1 !== 0 ? 2 : 0
    return `$${formatNumber(Math.abs(n), locale, decimals)}`
  }

  return (
    <>
      <div className="mb-5">
        <label htmlFor="apstatementstab-seleccionar-proveedor" className="block text-sm font-medium text-content mb-1">Seleccionar proveedor</label>
        <div className="max-w-md">
          <Autocomplete id="apstatementstab-seleccionar-proveedor"
            items={supplierItems}
            value={supplierId}
            displayValue={displayText}
            onChange={(val) => {
              if (!val) { setSupplierId(''); setDisplayText('') }
            }}
            onSelect={(item) => {
              setSupplierId(item.id)
              setDisplayText(`${item.label} — ${item.sublabel || ''}`)
            }}
            placeholder="Buscar por nombre o RUT..."
            className={inputCls}
          />
        </div>
      </div>

      {isLoading && <p className="text-center text-sm text-content-subtle py-8">Cargando estado de cuenta...</p>}

      {!supplierId && !isLoading && (
        <div className="flex flex-col items-center justify-center py-12 text-content-subtle">
          <FileText size={40} className="mb-3 text-content-subtle" />
          <p className="text-sm">Seleccione un proveedor para ver su estado de cuenta.</p>
        </div>
      )}

      {data && supplierId && (
        <>
          <div className="flex flex-wrap gap-4 mb-4 text-sm">
            <span className="text-content-muted">Período: {formatDate(data.date_from, locale)} — {formatDate(data.date_to, locale)}</span>
            <span className="font-medium">
              Saldo: <span className="text-danger-content-soft font-bold">
                ${formatNumber(Math.abs(parseFloat(data.closing_balance)), locale, 0)}
              </span>
            </span>
          </div>

          <DataTable
            columns={[
              { key: 'fecha', header: 'Fecha', render: e => formatDate(e.date, locale) },
              {
                key: 'tipo', header: 'Tipo',
                render: e => (
                  <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${e.type === 'invoice' ? 'bg-danger-surface-strong text-danger-content' : 'bg-success-surface-strong text-success-content'}`}>
                    {e.type === 'invoice' ? 'Factura' : 'Pago'}
                  </span>
                ),
              },
              { key: 'descripcion', header: 'Descripción', render: e => e.description },
              { key: 'debito', header: 'Débito', numeric: true, render: e => fmt(e.debit) },
              {
                key: 'credito', header: 'Crédito', numeric: true,
                render: e => <span className="text-success-content-soft">{fmt(e.credit)}</span>,
              },
              {
                key: 'saldo', header: 'Saldo', numeric: true,
                render: e => `$${formatNumber(parseFloat(e.balance), locale, 0)}`,
              },
            ]}
            rows={data.entries}
            rowKey={(_, i) => i}
            footer={{
              debito: fmt(data.total_debit),
              credito: <span className="text-success-content-soft">{fmt(data.total_credit)}</span>,
              saldo: `$${formatNumber(parseFloat(data.closing_balance), locale, 0)}`,
            }}
            empty={<EmptyState icon={FileText} size="sm" title="Sin movimientos en el período." />}
            minWidth={760}
            caption="Estado de cuenta del proveedor"
          />
        </>
      )}
    </>
  )
}
